/**
 * Overdue (PRD 5.4): every card past its deadline across the organisation,
 * latest first, and each site's work in progress at a glance. For HR,
 * managers, admins and compliance, filterable by site and department.
 *
 * The site strip doubles as the site filter: pressing a site narrows the list
 * to it, pressing it again shows every site. Each row opens the card, where
 * whoever oversees it can poke, reassign or skip it.
 */

import { useState } from "react";
import type { OverdueCard } from "@utuwrk/core";
import { useOrg } from "../lib/org.js";
import { durationText, plural, words } from "../lib/words.js";
import { EmptyState, Link, Loading, PageHeader, Problem, SlaPill, useLoad } from "./bits.js";

export function Overdue() {
  const { api } = useOrg();
  const { data, error } = useLoad(() => api.overdue(), [api]);
  const [site, setSite] = useState<string | null>(null);
  const [department, setDepartment] = useState("");

  if (!data && !error) return <Loading title="Overdue" />;
  const cards = data?.cards ?? [];
  const departments = [...new Set(cards.map((entry) => entry.card.department))].sort();
  const shown = cards.filter(
    (entry) =>
      (site === null || entry.siteId === site) && (!department || entry.card.department === department),
  );
  const sites = data?.sites ?? [];

  return (
    <section className="page">
      <PageHeader
        title="Overdue"
        meta={data ? (cards.length ? `${plural(cards.length, "card")} past due` : "Nothing is late") : null}
      />
      <Problem>{error}</Problem>

      {sites.length > 1 || (sites.length === 1 && sites[0]!.onboarding + sites[0]!.offboarding > 0) ? (
        <ul className="site-loads" aria-label="Work in progress by site">
          {sites.map((load) => (
            <li key={load.siteId}>
              <button
                type="button"
                className="card site-load"
                aria-pressed={site === load.siteId}
                onClick={() => setSite(site === load.siteId ? null : load.siteId)}
              >
                <span className="card-title">{load.siteName}</span>
                <span className="card-meta">
                  <span>{plural(load.onboarding, "starting", "starting")}</span>
                  <span>{plural(load.offboarding, "leaving", "leaving")}</span>
                  {load.late ? <span className="pill s-danger">{load.late} late</span> : <span>On time</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {cards.length > 0 ? (
        <div className="controls">
          <label className="field">
            Department
            <select value={department} onChange={(event) => setDepartment(event.target.value)}>
              <option value="">Every department</option>
              {departments.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {data && cards.length === 0 ? (
        <EmptyState icon="check-circle" title="Nothing is late">
          Cards past their deadline show here, across every site.
        </EmptyState>
      ) : null}
      {cards.length > 0 && shown.length === 0 ? (
        <p className="empty-line">Nothing late here. Choose another site or department.</p>
      ) : null}
      {shown.length > 0 ? (
        <ul className="cards">
          {shown.map((entry) => (
            <OverdueRow key={`${entry.instanceId}:${entry.card.key}`} entry={entry} />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function OverdueRow({ entry }: { entry: OverdueCard }) {
  const { nameOf } = useOrg();
  const { card } = entry;
  const holder = card.full && card.assigneeId !== null ? nameOf(card.assigneeId) : null;
  return (
    <li>
      <Link to={{ name: "card", id: entry.instanceId, key: card.key }} className="card card-link">
        <div className="card-top">
          <span className="card-dot s-danger" aria-hidden="true" />
          <h2 className="card-title">{card.title}</h2>
          <SlaPill sla={entry.status} />
        </div>
        <p className="card-meta">
          <span className="card-who">{entry.employeeName}</span>
          <span>{words(entry.kind)}</span>
          <span className="chip">{entry.siteName}</span>
          <span className="chip">{card.department}</span>
          <span>{holder ? `Held by ${holder}` : `Unassigned: anyone in ${card.department}`}</span>
          <span className="warn">{durationText(entry.lateBy)} late</span>
          {entry.flagged ? <span className="pill s-warn">Holder has left</span> : null}
          {card.state === "not_started" ? <span>Waiting on another card</span> : null}
        </p>
      </Link>
    </li>
  );
}
